import { useEffect } from "react";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { useFaust } from "../../contexts/FaustContext";
import { useFaustInit } from "../../hooks/useFaustInit";
import { LeftPanel } from "../panels/LeftPanel";
import { CenterPanel } from "../panels/CenterPanel";
import { RightPanel } from "../panels/RightPanel";
import { Loader2 } from "lucide-react";

export function IDELayout() {
    useFaustInit();
    const { state } = useFaust();

    // Handle responsive panel visibility
    useEffect(() => {
        const handleResize = () => {
            // Panels auto-collapse on small screens via the Panel component
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    if (!state.isReady) {
        return (
            <div className="h-screen w-screen flex flex-col items-center justify-center bg-black gap-6">
                <div className="relative">
                    <div className="absolute inset-0 bg-[#0078d4]/20 blur-3xl rounded-full" />
                    <div className="relative flex flex-col items-center gap-4">
                        <h1 className="text-4xl font-display font-bold tracking-tight text-[#cccccc]">
                            Faust IDE
                        </h1>
                        <div className="flex items-center gap-3 text-[#8b949e]">
                            <Loader2 className="h-4 w-4 animate-spin text-[#0078d4]" />
                            <span className="text-sm font-mono tracking-wider uppercase">
                                Initializing compiler
                            </span>
                        </div>
                    </div>
                </div>
                <div className="w-48 h-px bg-gradient-to-r from-transparent via-[#ffffff15] to-transparent" />
            </div>
        );
    }

    return (
        <div className="h-screen w-screen overflow-hidden bg-black">
            <PanelGroup direction="horizontal" className="h-full">
                {/* Left Panel - File Manager & Controls */}
                {state.showLeftPanel && (
                    <>
                        <Panel
                            id="left-panel"
                            order={1}
                            defaultSize={15}
                            minSize={12}
                            maxSize={25}
                            className="bg-black"
                        >
                            <LeftPanel />
                        </Panel>
                        <PanelResizeHandle className="w-[3px] bg-[#181818] hover:bg-[#0078d4]/30 transition-colors duration-150 relative group">
                            <div className="absolute top-1/2 -translate-y-1/2 left-0 w-full flex flex-col gap-0.5 items-center opacity-0 group-hover:opacity-100 transition-opacity">
                                <div className="w-0.5 h-1 rounded-full bg-[#0078d4]/50" />
                                <div className="w-0.5 h-1 rounded-full bg-[#0078d4]/50" />
                                <div className="w-0.5 h-1 rounded-full bg-[#0078d4]/50" />
                            </div>
                        </PanelResizeHandle>
                    </>
                )}

                {/* Center Panel - Editor & Bottom Tabs */}
                <Panel id="center-panel" order={2} minSize={40}>
                    <CenterPanel />
                </Panel>

                {/* Right Panel - Audio I/O & DSP Info */}
                {state.showRightPanel && (
                    <>
                        <PanelResizeHandle className="w-[3px] bg-[#181818] hover:bg-[#0078d4]/30 transition-colors duration-150 relative group">
                            <div className="absolute top-1/2 -translate-y-1/2 left-0 w-full flex flex-col gap-0.5 items-center opacity-0 group-hover:opacity-100 transition-opacity">
                                <div className="w-0.5 h-1 rounded-full bg-[#0078d4]/50" />
                                <div className="w-0.5 h-1 rounded-full bg-[#0078d4]/50" />
                                <div className="w-0.5 h-1 rounded-full bg-[#0078d4]/50" />
                            </div>
                        </PanelResizeHandle>
                        <Panel
                            id="right-panel"
                            order={3}
                            defaultSize={15}
                            minSize={12}
                            maxSize={30}
                            className="bg-black"
                        >
                            <RightPanel />
                        </Panel>
                    </>
                )}
            </PanelGroup>
        </div>
    );
}
